import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDisabledValueChainNames } from '../shared/hooks/useDisabledValueChainNames';
import { useValueChainMetaInfo } from '../shared/hooks/useValueChainMetaInfo';
import { useAuth } from '../../components/auth/AuthProvider';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SankeyDiagram from '../shared/components/SankeyDiagram';
import { getNodeTypeColor } from '../shared/utils/colors';
import FeatureNavTabs from '../shared/components/FeatureNavTabs';
import CuratedNodeDetails from '../shared/components/CuratedNodeDetails';
import fullValueChainService from './fullValueChainService';
import { api, toUserMessage } from '../../api/setup';
import { getSourcesAndExamples } from '../shared/utils/sources';

const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';

// Types
interface GraphNode {
  element_id: string;
  name: string;
  labels?: string[];
  properties?: Record<string, any>;
  n_iteration?: number;
  hs_code?: string[];
  hs_code_description?: string[];
  function?: string;
  source_db?: string;
}

interface SankeyData {
  nodes: Array<{
    name: any;
    value?: number;
    itemStyle?: { color: string };
    depth?: number;
    label?: any;
    nodeData: {
      type: any;
      properties: any;
      elementId: any;
      n_iteration: any;
    };
  }>;
  links: any[];
}

interface NodeDetails {
  element_id: string;
  name: string;
  labels?: string[];
  properties?: Record<string, any>;
  n_iteration?: number;
  hs_code?: string[];
  hs_code_description?: string[];
  function?: string;
  type?: string;
}

interface SectionExpanded {
  [key: string]: boolean;
}

const FullValueChainDiagram: React.FC = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const [selectedElementId, setSelectedElementId] = useState<string>('');
  const [graphData, setGraphData] = useState<SankeyData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [availableNodes, setAvailableNodes] = useState<GraphNode[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [maxIterations, setMaxIterations] = useState<number>(isMobile ? 2 : 4);
  const [minFlowThreshold, setMinFlowThreshold] = useState<number>(0.003); // 1% default threshold

  const [nodeNameToElementId, setNodeNameToElementId] = useState<Map<string, string>>(new Map());
  const [navigationHistory, setNavigationHistory] = useState<Array<{elementId: string, name: string}>>([]);
  const [selectedNodeDetails, setSelectedNodeDetails] = useState<NodeDetails | null>(null);
  const [loadingIncoming, setLoadingIncoming] = useState<boolean>(false);
  const [incomingNodes, setIncomingNodes] = useState<any[]>([]);
  const [sectionExpanded, setSectionExpanded] = useState<SectionExpanded>({});
  // Persist the initially selected root for stable dropdown label
  const [selectedRootElementId, setSelectedRootElementId] = useState<string>('');
  const [selectedRootName, setSelectedRootName] = useState<string>('');
  const [selectedRootSourceDb, setSelectedRootSourceDb] = useState<string>('');
  const STORAGE_ID_KEY = 'vc.selectedRootId';
  const STORAGE_NAME_KEY = 'vc.selectedRootName';
  const STORAGE_SOURCE_DB_KEY = 'vc.selectedRootSourceDb';

  // Fetch value chain meta info (info and shortcomings) for the selected root using source_db
  const { info: valueChainInfo, shortcomings: valueChainShortcomings } = useValueChainMetaInfo(selectedRootSourceDb);
  
  // Names in the dropdown that should be visible but not selectable (greyed out)
  const disabledValueChainNames = useDisabledValueChainNames();

  // Convert graph data to ECharts Sankey format
  const convertToSankeyFormat = useCallback((data) => {
    // Helper function to get node colors based on type using MUI theme
    const getNodeColor = (nodeType) => getNodeTypeColor(nodeType, theme);

        if (!data || !data.nodes || !data.relationships) {
      return { nodes: [], links: [] };
    }

    // Create a map of node IDs to names for easy lookup
    const nodeMap = new Map();
    const nodeNameCount = new Map();
    
    // Process nodes and handle duplicate names
    data.nodes.forEach(node => {
      const originalName = node.properties.name || `Node ${node.id}`;
      let finalName = originalName;
      
      // Handle duplicate names by appending a number
      if (nodeNameCount.has(originalName)) {
        const count = nodeNameCount.get(originalName) + 1;
        nodeNameCount.set(originalName, count);
        finalName = `${originalName} (${count})`;
      } else {
        nodeNameCount.set(originalName, 1);
      }
      
      nodeMap.set(node.id, {
        name: finalName,
        type: node.labels[0] || 'Unknown',
        n_iteration: node.properties.n_iteration || 0,
        properties: node.properties, // Store all original properties
        elementId: node.id // Store element ID for reference
      });
    });

    // Create nodes array for ECharts with explicit sizing
    const sankeyNodes = Array.from(nodeMap.values()).map(node => ({
      name: node.name,
      value: 0, // Will be calculated based on flows
      itemStyle: {
        color: getNodeColor(node.type)
      },
      // Store additional data for tooltip and click handling
      nodeData: {
        type: node.type,
        properties: node.properties,
        elementId: node.elementId,
        n_iteration: node.n_iteration
      }
    }));

    // Store mapping from node names to element IDs for click handling
    const nameToElementIdMap = new Map();
    Array.from(nodeMap.entries()).forEach(([elementId, node]) => {
      nameToElementIdMap.set(node.name, elementId);
    });
    setNodeNameToElementId(nameToElementIdMap);

    // Process relationships to create links
    const sankeyLinks = [];
    const processedLinks = new Set(); // To avoid duplicate links
    
    data.relationships.forEach(rel => {
      // Include PART_OF, TYPE_OF_SYSTEM, and GROUPED_PATH relationships
      if (rel.type !== 'PART_OF' && rel.type !== 'TYPE_OF_SYSTEM' && rel.type !== 'GROUPED_PATH') {
        return;
      }
      
      const sourceNode = nodeMap.get(rel.start_node_id);
      const targetNode = nodeMap.get(rel.end_node_id);
      
      if (sourceNode && targetNode) {
        const linkKey = `${sourceNode.name}->${targetNode.name}`;
        
        // Avoid duplicate links
        if (!processedLinks.has(linkKey)) {
          processedLinks.add(linkKey);
          
          // Default link color derives from source node color per ECharts when set to 'source'.
          // We override specifically when the source node is a TechnologyType to use its node color with opacity.
          const sourceIsTechnologyType = String(sourceNode.type) === 'TechnologyType';
          const sourceTechColor = getNodeColor('TechnologyType');

          sankeyLinks.push({
            source: sourceNode.name,
            target: targetNode.name,
            value: rel.properties.path_weight || 0,  // Use original values
            lineStyle: sourceIsTechnologyType
              ? {
                  color: sourceTechColor,
                  opacity: 0.45
                }
              : {
                  color: 'source'
                }
          });
        }
      }
    });

    // Debug logging (optional)
    const targetNode = sankeyNodes.find(node => node.name.includes('Grid-Connected'));
    if (targetNode) {
      if (SHOULD_DEBUG) {
        console.log('Target node:', targetNode.name);
        console.log('Incoming flows to target node:');
      }
      sankeyLinks.forEach(link => {
        if (link.target === targetNode.name) {
          if (SHOULD_DEBUG) console.log(`  ${link.source} -> ${link.target}: ${link.value}`);
        }
      });
      const totalIncoming = sankeyLinks
        .filter(link => link.target === targetNode.name)
        .reduce((sum, link) => sum + link.value, 0);
      if (SHOULD_DEBUG) console.log('Total incoming flow:', totalIncoming);
    }

    return {
      nodes: sankeyNodes,
      links: sankeyLinks
    };
  }, [theme.palette]);

  // DEVELOPMENT MODE: Load all value chains from API instead of permission-filtered
  // TODO: Restore permission-based filtering when re-enabling permissions
  useEffect(() => {
    // Load all available roots from API with authentication
    (async () => {
      try {
        if (SHOULD_DEBUG) console.log('Fetching nodes with authentication');
        const response = await api.graphData.getAvailableRootNodesApiV1GraphRootsGet();
        if (SHOULD_DEBUG) console.log('Raw nodes API response:', response);
        
        // API returns { success: true, data: [...], message: "..." }
        // Extract the actual data array
        const roots = Array.isArray(response?.data) ? response.data : 
                     Array.isArray(response) ? response : [];
        
        if (SHOULD_DEBUG) {
          console.log('Processed nodes:', roots);
          console.log('Number of nodes:', roots.length);
        }
        setAvailableNodes(roots);
        
        // If current selection is no longer available (e.g., after logout), clear it and select first public one
        if (selectedElementId) {
          const isStillAvailable = roots.some((r:any) => String(r.element_id) === String(selectedElementId));
          if (!isStillAvailable) {
            if (SHOULD_DEBUG) console.log('Selected root no longer available, selecting first available public root');
            setSelectedElementId('');
            setSelectedRootElementId('');
            setSelectedRootName('');
            setSelectedRootSourceDb('');
            setGraphData(null);
            // Automatically select first available public root
            if (roots.length > 0) {
              const firstPublic = roots[0];
              if (SHOULD_DEBUG) console.log('Auto-selecting first available root after logout:', firstPublic);
              const firstId = String(firstPublic.element_id);
              const firstName = String(firstPublic.name);
              const firstSourceDb = String(firstPublic.source_db || '');
              setSelectedElementId(firstId);
              setSelectedRootElementId(firstId);
              setSelectedRootName(firstName);
              setSelectedRootSourceDb(firstSourceDb);
              setNavigationHistory([{ elementId: firstPublic.element_id, name: firstPublic.name }]);
            }
          }
        }
        
        const savedId = String(localStorage.getItem(STORAGE_ID_KEY) || '');
        const savedName = String(localStorage.getItem(STORAGE_NAME_KEY) || '');
        const savedSourceDb = String(localStorage.getItem(STORAGE_SOURCE_DB_KEY) || '');
        if (!selectedElementId) {
          if (savedId) {
            const matchedRoot = roots.find((r:any)=>String(r.element_id)===savedId);
            if (matchedRoot) {
              setSelectedElementId(savedId);
              setSelectedRootElementId(savedId);
              setSelectedRootName(savedName || (matchedRoot?.name || ''));
              setSelectedRootSourceDb(savedSourceDb || (matchedRoot?.source_db || ''));
              setNavigationHistory([{ elementId: savedId, name: savedName || (matchedRoot?.name || `Node ${savedId}`) }]);
            } else if (roots.length > 0) {
              // If saved root not available, select first available
              const first = roots[0];
              if (SHOULD_DEBUG) console.log('Saved root not available, selecting first available:', first);
              const firstId = String(first.element_id);
              const firstName = String(first.name);
              const firstSourceDb = String(first.source_db || '');
              setSelectedElementId(firstId);
              setSelectedRootElementId(firstId);
              setSelectedRootName(firstName);
              setSelectedRootSourceDb(firstSourceDb);
              setNavigationHistory([{ elementId: first.element_id, name: first.name }]);
            }
          } else if (roots.length > 0) {
            const first = roots[0];
            if (SHOULD_DEBUG) console.log('Setting first node:', first);
            const firstId = String(first.element_id);
            const firstName = String(first.name);
            const firstSourceDb = String(first.source_db || '');
            setSelectedElementId(firstId);
            setSelectedRootElementId(firstId);
            setSelectedRootName(firstName);
            setSelectedRootSourceDb(firstSourceDb);
            setNavigationHistory([{ elementId: first.element_id, name: first.name }]);
          }
        }
      } catch (error) {
        if (SHOULD_DEBUG) console.error('Error loading nodes:', error);
        setAvailableNodes([]);
      }
    })();
  }, [isAuthenticated]); // Reload when authentication state changes

  /* ORIGINAL PERMISSION-BASED CODE - Uncomment to restore
  useEffect(() => {
    if (accessibleValueChains && accessibleValueChains.length > 0) {
      if (SHOULD_DEBUG) console.log('Setting available nodes from accessible value chains:', accessibleValueChains);
      setAvailableNodes(accessibleValueChains);
      if (!selectedElementId && accessibleValueChains.length > 0) {
        const first = accessibleValueChains[0];
        setSelectedElementId(String(first.element_id));
        setNavigationHistory([{ elementId: first.element_id, name: first.name }]);
      }
    } else {
      setAvailableNodes([]);
    }
  }, [accessibleValueChains, selectedElementId]);
  */

  // Fetch graph data when element ID changes
  useEffect(() => {
    if (!selectedElementId) {
      setGraphData(null);
      return;
    }


    const fetchGraphData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await fullValueChainService.getGraphData(selectedElementId, maxIterations, minFlowThreshold);
        const sankeyData = convertToSankeyFormat(data);
        setGraphData(sankeyData);
      } catch (err) {
        const { message, correlationId } = toUserMessage(err);
        setError(correlationId ? `${message} (Ref: ${correlationId})` : message);
      } finally {
        setLoading(false);
      }
    };

    fetchGraphData();
  }, [selectedElementId, maxIterations, convertToSankeyFormat, availableNodes, isAuthenticated]);

  // Refetch only when threshold decreases (expand results)
  const prevThresholdRef = useRef(minFlowThreshold);
  useEffect(() => {
    if (minFlowThreshold < prevThresholdRef.current && selectedElementId) {
      // trigger same fetch as main effect
      (async () => {
        setLoading(true);
        setError(null);
        try {
          const data = await fullValueChainService.getGraphData(selectedElementId, maxIterations, minFlowThreshold);
          const sankeyData = convertToSankeyFormat(data);
          setGraphData(sankeyData);
        } catch (err) {
          const { message, correlationId } = toUserMessage(err);
          setError(correlationId ? `${message} (Ref: ${correlationId})` : message);
        } finally {
          setLoading(false);
        }
      })();
    }
    prevThresholdRef.current = minFlowThreshold;
  }, [minFlowThreshold, selectedElementId, maxIterations, convertToSankeyFormat]);

  // Auto-fetch node details when graph data changes and we have a selected element ID
  useEffect(() => {
    if (graphData && selectedElementId) {
      // Find the node data for the selected element ID
      let foundNodeData = null;
      graphData.nodes.forEach(node => {
        const nodeData = node.nodeData;
        if (nodeData && nodeData.elementId === selectedElementId) {
          foundNodeData = nodeData;
        }
      });
      
      // Only update if we found the node and it's different from current
      if (foundNodeData && (!selectedNodeDetails || selectedNodeDetails.element_id !== foundNodeData.element_id)) {
        setSelectedNodeDetails(foundNodeData);
        // Also fetch incoming relationships for this node
        fetchIncomingRelationships(selectedElementId);
      }
    }
  }, [graphData, selectedElementId, selectedNodeDetails]);

  // Refetch incoming relationships when threshold changes (for current selection)
  useEffect(() => {
    if (minFlowThreshold < prevThresholdRef.current && selectedElementId) {
      fetchIncomingRelationships(selectedElementId);
    }
  }, [minFlowThreshold, selectedElementId]);

  // Fetch incoming relationships for a specific node
  const fetchIncomingRelationships = async (elementId) => {
    setLoadingIncoming(true);
    try {
      const directIncoming = await fullValueChainService.getIncomingRelationships(elementId, minFlowThreshold);
      setIncomingNodes(directIncoming);
    } catch (err) {
      const { message } = toUserMessage(err);
      setError(message);
      setIncomingNodes([]);
    } finally {
      setLoadingIncoming(false);
    }
  };

  // Handle clicking on an incoming node
  const handleIncomingNodeClick = (elementId, nodeName) => {
    // Clear the detail card and incoming nodes first
    setSelectedNodeDetails(null);
    setIncomingNodes([]);
    
    // Then update the navigation state
    setSelectedElementId(elementId);
    setNavigationHistory(prev => [...prev, { elementId, name: nodeName }]);
  };

  const handleElementIdChange = (eventOrValue) => {
    const newElementId = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue?.target?.value;
    if (typeof newElementId === 'string' && newElementId.startsWith('disabled:')) {
      return; // ignore disabled placeholder clicks
    }
    setSelectedElementId(String(newElementId));
    
    // Clear existing details when making a new selection
    setSelectedNodeDetails(null);
    setIncomingNodes([]);
    
    // Add the initial node to navigation history if it's a new selection
    if (newElementId && (!navigationHistory.length || navigationHistory[navigationHistory.length - 1].elementId !== newElementId)) {
      // Find the node name from available nodes
      const selectedNode = availableNodes.find(node => node.element_id === newElementId);
      const nodeName = selectedNode ? selectedNode.name : `Node ${newElementId}`;
      const sourceDb = selectedNode?.source_db || '';
      // Persist the root selection for the dropdown label
      setSelectedRootElementId(String(newElementId));
      setSelectedRootName(String(nodeName));
      setSelectedRootSourceDb(String(sourceDb));
      try {
        localStorage.setItem(STORAGE_ID_KEY, String(newElementId));
        localStorage.setItem(STORAGE_NAME_KEY, String(nodeName));
        localStorage.setItem(STORAGE_SOURCE_DB_KEY, String(sourceDb));
      } catch {}
      setNavigationHistory([{ elementId: newElementId, name: nodeName }]);
    }
  };

  const handleNodeClick = (params) => {
    // Prefer elementId from node payload; fall back to name -> id map
    const payloadEl = params && params.data && params.data.nodeData ? params.data.nodeData.elementId : undefined;
    const byNameEl = nodeNameToElementId.get(params?.name);
    const elementId = String(payloadEl || byNameEl || '');

    if (!elementId) {
      if (SHOULD_DEBUG) console.warn('Could not find element ID for node:', params?.name);
      return;
    }

    // Store detailed node information for display
    setSelectedNodeDetails(params.data.nodeData);

    // Fetch incoming relationships for this node
    fetchIncomingRelationships(elementId);

    // Update the selected element ID to navigate to this node
    setSelectedElementId(elementId);

    // Add to navigation history
    setNavigationHistory(prev => [...prev, { elementId, name: params.name }]);
  };

  const handleNavigateBack = (index) => {
    if (index >= 0 && index < navigationHistory.length) {
      const targetHistory = navigationHistory[index];
      setSelectedElementId(targetHistory.elementId);
      // Truncate history to this point
      setNavigationHistory(prev => prev.slice(0, index + 1));
    }
  };

  const handleResetNavigation = () => {
    setNavigationHistory([]);
  };

  // Render curated details for System / Material / TechnologyType / NoProductTechnologyType
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderCuratedDetails = () => {
    if (!selectedNodeDetails) return null;
    const nodeType = selectedNodeDetails.type;
    if (nodeType === 'LaborCost' || nodeType === 'AdditionalInputCost' || nodeType === 'MachineryCost') {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">More info coming soon</Typography>
        </Box>
      );
    }
    const p = selectedNodeDetails.properties || {};
    const toSentence = (value) => {
      if (Array.isArray(value)) {
        const flat = value.flat().filter(Boolean).map(String).map(s => s.trim()).filter(s => s.length > 0);
        return flat.join('; ');
      }
      return typeof value === 'string' ? value : (value != null ? String(value) : '');
    };
    const toArray = (value) => {
      if (Array.isArray(value)) return value.filter(Boolean).map(v => String(v).trim()).filter(v => v.length > 0);
      if (typeof value === 'string') return value.split(/\s*;\s*|\s*\n\s*|\s*,\s*/).map(v => v.trim()).filter(v => v.length > 0);
      return [];
    };
    const isUrl = (s) => {
      if (typeof s !== 'string') return false;
      try { const u = new URL(s); return !!u.protocol && !!u.host; } catch { return s.startsWith('http://') || s.startsWith('https://'); }
    };

    // Decide field set based on node type
    let rows = [];
    let sections = null;

    if (nodeType === 'System' || nodeType === 'Material') {
      sections = [
        {
          title: 'Characteristics',
          preview: 2,
          rows: [
            { label: 'Function', value: toSentence(p.function) },
            { label: 'Mechanism', value: toSentence(p.mechanism) },
            { label: 'Specifications', value: toSentence(p.specifications) },
            { label: 'Core Components', value: toSentence(p.core_components) },
            { label: 'Source References', values: toArray(p.source_references) },
          ]
        },
        {
          title: 'Type Distinction',
          preview: 1,
          rows: [
            { label: 'Distinguishing Features of its types', value: toSentence(p.type_distinction) }
          ]
        }
      ];
    } else if (nodeType === 'TechnologyType' || nodeType === 'NoProductTechnologyType') {
      const { sourcesVal, sourcesLabel, exampleProductsLabel, exampleProductsValues } = getSourcesAndExamples(nodeType, p);
      const combinedPrice = [toSentence(p.price_range), toSentence(p.price_range_unit)].filter(s => s && s.trim().length > 0).join(' ');
      const codeList = toArray(p.hs_code);
      const codeDescList = toArray(p.hs_code_description);
      const codeWithDesc = codeList.length > 0 ? codeList.map((c, i) => (codeDescList[i] ? `${c} ${codeDescList[i]}` : c)) : [];
      sections = [
        {
          title: 'Characteristics',
          preview: 2,
          rows: [
            { label: 'Function', value: toSentence(p.function) },
            { label: 'Mechanism', value: toSentence(p.mechanism) },
            { label: 'Specifications', value: toSentence(p.specifications) },
            { label: 'Distinguishing Features', value: toSentence(p.distinguishing_features) },
            { label: 'Core Components', value: toSentence(p.core_components) },
            { label: sourcesLabel, values: toArray(sourcesVal) },
          ]
        },
        {
          title: 'Prevalence',
          preview: 2,
          rows: [
            { label: 'Description', value: toSentence(p.prevalence) },
            { label: 'Rating', value: toSentence(p.prevalence_rating) },
            { label: 'Prevalence Sources', values: toArray(p.prevalence_sources) },
          ]
        },
        {
          title: 'HS Codes',
          preview: 1,
          rows: [
            { label: 'HS Code', values: codeWithDesc },
            { label: 'HS Code Explanation', values: toArray(p.hs_code_explanation) },
          ]
        },
              {
                title: 'Examples',
                preview: 1,
                rows: [
                  { label: 'Price', value: combinedPrice },
                  exampleProductsLabel ? { label: exampleProductsLabel, values: toArray(exampleProductsValues) } : null,
                ].filter(Boolean)
              }
      ];
    } else {
      return null;
    }

    const isReviewTrue = (val) => val === true || val === 1 || (typeof val === 'string' && val.toLowerCase() === 'true');

    return (
      <Box>
        {/* Review flag alert */}
        {isReviewTrue(p.review_flag) && p.review_flag_description && String(p.review_flag_description).trim().length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {String(p.review_flag_description)}
          </Alert>
        )}

        {/* Curated fields */}
        {sections ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: (nodeType === 'System' || nodeType === 'Material') ? '1fr 1fr' : '1fr 1fr' }, gap: 1.5 }}>
            {sections.map((section, si) => {
              const expanded = !!sectionExpanded[si];
              return (
                <Box key={`sec-${si}`} sx={{ p: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
                    {section.title}
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={expanded ? {} : { maxHeight: '10.5em', overflow: 'hidden' }}>
                {section.rows.map((row, idx) => {
                  const val = row.value;
                  const vals = row.values;
                  if ((val == null || String(val).trim().length === 0) && (!Array.isArray(vals) || vals.length === 0)) return null;
                  return (
                    <Box key={`${section.title}-${row.label}-${idx}`} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{row.label}</Typography>
                      {Array.isArray(vals) && vals.length > 0 ? (
                        <Box sx={{ display: 'grid', gap: 0.25 }}>
                          {vals.map((v, i) => (
                            <Box key={`${section.title}-${row.label}-${idx}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {isUrl(v) ? (
                                <>
                                  <Typography component="a" href={v} target="_blank" rel="noopener noreferrer" variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{v}</Typography>
                                  <IconButton size="small" onClick={() => navigator.clipboard?.writeText(v)} aria-label="Copy link">
                                    <ContentCopyIcon fontSize="inherit" />
                                  </IconButton>
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{v}</Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{val}</Typography>
                      )}
                    </Box>
                  );
                })}
                    </Box>
                    {!expanded && (
                      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 24, background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(0,0,0,0.04))' }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <IconButton size="small" onClick={() => setSectionExpanded(prev => ({ ...prev, [si]: !expanded }))} aria-label={expanded ? 'Collapse' : 'Expand'} sx={{ mt: 0.5 }}>
                      {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          rows.map((row, idx) => {
            const val = row.value;
            const vals = row.values;
            if ((val == null || String(val).trim().length === 0) && (!Array.isArray(vals) || vals.length === 0)) return null;
            return (
              <Box key={`${row.label}-${idx}`} sx={{ mb: 1.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{row.label}</Typography>
                {Array.isArray(vals) && vals.length > 0 ? (
                  <Box sx={{ display: 'grid', gap: 0.5 }}>
                    {vals.map((v, i) => (
                      <Box key={`${row.label}-${idx}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isUrl(v) ? (
                          <>
                            <Typography component="a" href={v} target="_blank" rel="noopener noreferrer" variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{v}</Typography>
                            <IconButton size="small" onClick={() => navigator.clipboard?.writeText(v)} aria-label="Copy link">
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{v}</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{val}</Typography>
                )}
              </Box>
            );
          })
        )}
      </Box>
    );
  };

  // DEVELOPMENT MODE: Permission checks commented out for public access
  // TODO: Uncomment these blocks to re-enable permission checks
  /*
  // Show loading state while permissions are loading
  if (permissionsLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading value chain permissions...
        </Typography>
      </Box>
    );
  }

  // Show message if no accessible value chains
  if (!accessibleValueChains || accessibleValueChains.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Systems and their Types
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            No Value Chain Access
          </Typography>
          <Typography variant="body2">
            You don't have access to any value chains. Please contact your administrator to request access.
          </Typography>
        </Alert>
      </Box>
    );
  }
  */

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Systems and their Types
      </Typography>
      
      {/* Controls Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
        <Typography variant="body1" gutterBottom>Select a Value Chain to View its Full Breakdown into Systems and their Types</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Click on a node to move up the value chain and see a more detailed breakdown of the systems and their types.
        </Typography>
        <Divider sx={{ my: 2, opacity: 0.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' }, overflowX: { xs: 'visible', md: 'auto' } }}>
          {/* Value Chain Selection */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 320px' }, minWidth: { xs: '100%', md: 260 } }}>
            <Typography variant="body2" gutterBottom>
              Select a Value Chain
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={String(selectedRootElementId || '')}
                onChange={(e) => handleElementIdChange(e.target.value)}
                displayEmpty
                sx={{ bgcolor: 'background.paper' }}
                renderValue={(val) => {
                  const id = String(val || '');
                  if (selectedRootElementId && id === String(selectedRootElementId) && selectedRootName) {
                    return selectedRootName;
                  }
                  const n = availableNodes.find((x) => String(x.element_id) === id);
                  return n ? n.name : '';
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                {(() => {
                  const existingNames = new Set(availableNodes.map(n => n.name));
                  const placeholders = Array.from(disabledValueChainNames).filter(n => !existingNames.has(n));
                  return (
                    <>
                {availableNodes.map((node) => {
                  const isDisabled = disabledValueChainNames.has(node.name);
                  return (
                    <MenuItem
                      key={node.element_id}
                      value={String(node.element_id)}
                      disabled={isDisabled}
                      onClick={!isDisabled ? () => handleElementIdChange(String(node.element_id)) : undefined}
                      sx={isDisabled ? { color: theme.palette.text.disabled } : undefined}
                    >
                      {node.name}
                    </MenuItem>
                  );
                })}
                {placeholders.map((name) => (
                  <MenuItem
                    key={`disabled-${name}`}
                    disabled
                    sx={{ color: theme.palette.text.disabled }}
                  >
                    {name}
                  </MenuItem>
                ))}
                    </>
                  );
                })()}
              </Select>
            </FormControl>
          </Box>

          {/* Max Iterations Selector */}
          <Box sx={{ flex: '0 0 auto', minWidth: 150 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2">
                Additional Layers
              </Typography>
              <Tooltip
                placement="top-start"
                title={
                  <Typography variant="body2" sx={{ maxWidth: 420 }}>
                    The number of layers defines how many steps upstream in the value chain you want to view.
                  </Typography>
                }
              >
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box>
              <input
                type="number"
                min="1"
                max="5"
                value={maxIterations}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 5) {
                    setMaxIterations(value);
                  }
                }}
                style={{ 
                  width: '60px',
                  padding: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </Box>
          </Box>

          {/* Min Flow Threshold Selector */}
          <Box sx={{ flex: '0 0 auto', minWidth: 150 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2">
                Min Value
              </Typography>
              <Tooltip
                placement="top-start"
                title={
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 460 }}>
                    {'This filter removes very small flows from the chart.\n'}
                    {'Flows are combined for each source-to-target connection, then the minimum value is applied.\n'}
                    {'In other words, it keeps meaningful links and hides minor ones.'}
                  </Typography>
                }
              >
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ position: 'relative', width: '84px' }}>
              <input
                type="number"
                min="0.1"
                max="10"
                step="0.1"
                value={(minFlowThreshold * 100).toFixed(1)}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  if (value >= 0.1 && value <= 10) {
                    setMinFlowThreshold(value / 100);
                  }
                }}
                style={{ 
                  width: '100%',
                  padding: '8px 24px 8px 8px',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  lineHeight: 1,
                }}
              >
                %
              </Typography>
            </Box>
          </Box>

          {/* Navigation History */}
          {navigationHistory.length > 0 && (
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 420px' }, minWidth: { xs: '100%', md: 260 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Navigation History
                </Typography>
                <Tooltip title="Reset Navigation">
                  <IconButton 
                    size="small" 
                    onClick={handleResetNavigation}
                    sx={{ color: theme.palette.error.main }}
                  >
                    ✕
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {navigationHistory.map((item, index) => (
                  <Chip
                    key={index}
                    label={item.name}
                    onClick={() => handleNavigateBack(index)}
                    color={index === navigationHistory.length - 1 ? "primary" : "default"}
                    variant={index === navigationHistory.length - 1 ? "filled" : "outlined"}
                    size="small"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
        <Divider sx={{ my: 2, opacity: 0.5 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The number of layers defines how many steps upstream in the value chain you want to view.
          The minimum value threshold specifies the lowest percentage a valueflow must have in order to be represented.
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Sankey Diagram */}
      <SankeyDiagram
        data={graphData}
        loading={loading}
        error={error}
        onNodeClick={handleNodeClick}
        title={`${(navigationHistory[navigationHistory.length - 1]?.name) || (availableNodes.find(n => n.element_id === selectedElementId)?.name) || 'Selected'} Value Chain (Systems and their Types)`}
        minFlowThreshold={minFlowThreshold}
        rootElementId={selectedElementId}
        requireRootConnectivity
        enablePersistentFocus={false}
        valueChainInfo={valueChainInfo}
        valueChainShortcomings={valueChainShortcomings}
        legend={(
          <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {(() => {
              const techColor = getNodeTypeColor('TechnologyType', theme);
              const materialColor = getNodeTypeColor('Material', theme);
              const componentColor = getNodeTypeColor('System', theme);
              const otherColor = theme.palette.grey[500];

              const Swatch = ({ color }) => (
                <Box sx={{ width: 28, height: 6, borderRadius: 3, bgcolor: color, opacity: 0.45, border: '1px solid', borderColor: (t) => t.palette.divider, mr: 1 }} />
              );

              return (
                <>
                  <Box sx={{ gridColumn: '1 / -1', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block' }}>Legend (flows)</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Each component or material can appear in the form of several technology types, with a prevalence weight that tells us how common that type is.

Each technology type is made up of different components/materials, each with an estimated cost-share weight that approximates the relative cost share of that component in the mix of other components, materials, labor, etc.

These weights inform the size of the flows between technology types and components/materials.
                    </Typography>
                  </Box>
                  
                  <Paper elevation={1} sx={{ p: 1.25, bgcolor: 'rgba(255,255,255,0.35)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Prevalence (of types)</Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                      <Swatch color={techColor} />
                      <Typography variant="caption" color="text.secondary">prevalence of type</Typography>
                    </Box>
                  </Paper>
                  <Paper elevation={1} sx={{ p: 1.25, bgcolor: 'rgba(255,255,255,0.35)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Cost shares (relative)</Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Swatch color={materialColor} />
                        <Typography variant="caption" color="text.secondary">material</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Swatch color={componentColor} />
                        <Typography variant="caption" color="text.secondary">component</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Swatch color={otherColor} />
                        <Typography variant="caption" color="text.secondary">other</Typography>
                      </Box>
                    </Box>
                  </Paper>
                  
                </>
              );
            })()}
          </Box>
        )}
      />

      {/* Local Navigation Tabs directly under the Sankey */}
          <FeatureNavTabs />

      {/* Node Details */}
      {selectedNodeDetails && (
        <>
        <CuratedNodeDetails node={{ type: selectedNodeDetails.type as any, properties: selectedNodeDetails.properties || {} }} />
        <Paper elevation={1} sx={{ p: 3, mt: 2, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, minWidth: 0, overflow: 'hidden' }}>
          {/* Incoming Nodes Section */}
          <Typography variant="subtitle1" gutterBottom>
            Incoming Nodes
          </Typography>
          {loadingIncoming ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : incomingNodes.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(auto-fit, minmax(260px, 1fr))' }, gap: 2, minWidth: 0 }}>
              {incomingNodes.map((item, index) => (
                <Paper key={index} elevation={1} sx={{ p: 2, cursor: 'pointer', bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, minWidth: 0, overflow: 'hidden' }} 
                       onClick={() => handleIncomingNodeClick(item.sourceNode.id, item.sourceNode.properties?.name || `Node ${item.sourceNode.id}`)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {item.sourceNode.properties?.name || `Node ${item.sourceNode.id}`}
                    </Typography>
                    <Chip 
                      label={item.sourceNode.labels?.[0] || 'Node'} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.875rem' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Type</Typography>
                      <Typography variant="body2">{item.sourceNode.labels?.[0] || 'Unknown'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Weight</Typography>
                      <Typography variant="body2">{item.weight}</Typography>
                    </Box>
                  </Box>
                  {/* Element ID intentionally hidden */}
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No incoming nodes found.
            </Typography>
          )}
        </Paper>
        </>
      )}

      {!selectedElementId && !loading && (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Select a node from the dropdown to view its incoming relationships
          </Typography>
        </Paper>
      )}

      {graphData && !loading && graphData.links.length === 0 && (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No relationships found for this node. Try selecting a different node.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default FullValueChainDiagram;

